import React, { useState } from "react";
import { Box, Typography, CircularProgress, Alert } from "@mui/material";
import { Store as StoreIcon } from "@mui/icons-material";
import { ShopCard } from "./ShopCard";
import { ShopDetailsDialog } from "./ShopDetailsDialog";
import type { ShopDetailsDialogData } from "./ShopDetailsDialog";
import { shortenTime } from "../../../../../utils/shopUtils";

interface TrustedShopsListProps {
  closestShops: Array<{
    shop: {
      id: number;
      name: string;
      address: string;
      phoneNumber?: string | null;
      workingHours?: string | null;
      description?: string | null;
      website?: string | null;
    };
    aerialDistance: number;
    distanceText: string;
    drivingDistance?: string;
    drivingDuration?: string;
  }>;
  isLoading: boolean;
  isCalculatingDriving?: boolean;
  error: string | null;
  onCenterShop?: (shop: any) => void;
}

export const TrustedShopsList: React.FC<TrustedShopsListProps> = ({
  closestShops,
  isLoading,
  isCalculatingDriving = false,
  error,
  onCenterShop,
}) => {
  const [detailsDialog, setDetailsDialog] =
    useState<ShopDetailsDialogData | null>(null);

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 1 }}>
        {error}
      </Alert>
    );
  }

  if (isLoading || isCalculatingDriving) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
          gap: 1,
        }}
      >
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          {isCalculatingDriving
            ? "Calculating driving distances..."
            : "Loading shops..."}
        </Typography>
      </Box>
    );
  }

  if (closestShops.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 3,
          textAlign: "center",
        }}
      >
        <StoreIcon sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          No Trusted Shops
        </Typography>
        <Typography variant="caption" color="text.disabled">
          No trusted shops found in this area
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ maxHeight: 220, overflowY: "auto", pr: 0.5, pb: 0.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {closestShops.map((result) => (
            <ShopCard
              key={result.shop.id}
              data={{
                type: "trusted",
                shop: result.shop,
                aerialDistance: result.aerialDistance,
                distanceText: result.distanceText,
                drivingDistance: result.drivingDistance,
                drivingDuration: result.drivingDuration,
              }}
              onCenterShop={onCenterShop}
              onShowDetailsDialog={setDetailsDialog}
              shortenTime={shortenTime}
            />
          ))}
        </Box>
      </Box>
      <ShopDetailsDialog
        open={Boolean(detailsDialog)}
        details={detailsDialog}
        onClose={() => setDetailsDialog(null)}
      />
    </>
  );
};
